/**
 * Font family options + normalization (aligned with AgeShield TemplateEditorPage).
 * Used in admin selects, WidgetPreview, and mirrored in calendar-widget-runtime.js.
 */

export const DEFAULT_FONT_STACK = "'Inter', sans-serif";

export const FONT_FAMILY_OPTIONS = [
  { label: "Sans", value: "Arial, sans-serif" },
  { label: "Serif", value: "'Source Serif Pro', Georgia, serif" },
  { label: "Monospace", value: "'Source Code Pro', 'Courier New', monospace" },
  { label: "Inter", value: "'Inter', sans-serif" },
  { label: "Poppins", value: "'Poppins', sans-serif" },
  { label: "Roboto", value: "'Roboto', sans-serif" },
  { label: "Open Sans", value: "'Open Sans', sans-serif" },
  { label: "Lato", value: "'Lato', sans-serif" },
  { label: "Merriweather", value: "'Merriweather', serif" },
  { label: "Courier New", value: "'Courier New', monospace" },
];

/** Calendar legacy stored values → canonical stacks. */
export const LEGACY_FONT_VALUE_MAP = {
  Inter: "'Inter', sans-serif",
  "Inter, sans-serif": "'Inter', sans-serif",
  "system-ui": "Arial, sans-serif",
  Georgia: "'Source Serif Pro', Georgia, serif",
  "Georgia, serif": "'Source Serif Pro', Georgia, serif",
  "Helvetica, Arial, sans-serif": "Arial, sans-serif",
  "Times New Roman": "'Times New Roman', serif",
  "'Times New Roman', serif": "'Times New Roman', serif",
  "ui-monospace": "'Source Code Pro', 'Courier New', monospace",
};

/** Google Fonts families to load on preview / storefront (system stacks excluded). */
const GOOGLE_FONT_NAMES = new Set([
  "Inter",
  "Poppins",
  "Roboto",
  "Open Sans",
  "Lato",
  "Merriweather",
  "Source Serif Pro",
  "Source Code Pro",
]);

export function decodeHtmlEntitiesString(input) {
  let s = String(input ?? "").trim();
  if (!s) return "";
  if (typeof document !== "undefined") {
    const ta = document.createElement("textarea");
    for (let i = 0; i < 6; i++) {
      ta.innerHTML = s;
      const next = String(ta.value).trim();
      if (next === s) break;
      s = next;
    }
    return s;
  }
  for (let i = 0; i < 6; i++) {
    const next = s
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#x22;/gi, '"')
      .replace(/&#0*34;/g, '"')
      .replace(/&#x27;/gi, "'")
      .replace(/&#0*39;/g, "'")
      .replace(/&apos;/gi, "'")
      .replace(/&amp;/g, "&");
    if (next === s) break;
    s = next;
  }
  return s.trim();
}

function fontKeyForMatch(s) {
  return String(s ?? "")
    .trim()
    .replace(/\s*,\s*/g, ",")
    .toLowerCase();
}

export function resolveFontSelectStored(stored) {
  const raw = decodeHtmlEntitiesString(String(stored ?? ""));
  if (!raw) return DEFAULT_FONT_STACK;
  const exact = FONT_FAMILY_OPTIONS.find((o) => o.value === raw);
  if (exact) return exact.value;
  const fk = fontKeyForMatch(raw);
  const byCanon = FONT_FAMILY_OPTIONS.find((o) => fontKeyForMatch(o.value) === fk);
  if (byCanon) return byCanon.value;
  const mapped = LEGACY_FONT_VALUE_MAP[raw];
  if (mapped) {
    const mHit = FONT_FAMILY_OPTIONS.find((o) => o.value === mapped);
    if (mHit) return mHit.value;
    return mapped;
  }
  const legacyKey = Object.keys(LEGACY_FONT_VALUE_MAP).find((k) => fontKeyForMatch(k) === fk);
  if (legacyKey) return LEGACY_FONT_VALUE_MAP[legacyKey];
  return raw;
}

export function fontSelectOptions(stored) {
  const v = resolveFontSelectStored(stored);
  if (FONT_FAMILY_OPTIONS.some((o) => o.value === v)) return FONT_FAMILY_OPTIONS;
  return [{ label: "Custom font", value: v }, ...FONT_FAMILY_OPTIONS];
}

export function normalizeFontSelectValue(stored) {
  return resolveFontSelectStored(stored);
}

/** Resolved CSS font-family for inline styles (preview + storefront). */
export function resolveFontFamilyStack(stored, fallback = DEFAULT_FONT_STACK) {
  const v = resolveFontSelectStored(stored);
  return v || fallback;
}

/** Single calendar-wide font (Appearance → Calendar setting). Legacy `eventsFontFamily` is fallback. */
export const resolveCalendarFontFamily = (appearance, fallback = DEFAULT_FONT_STACK) => {
  const stored =
    String(appearance?.calendarFontFamily ?? "").trim() ||
    String(appearance?.eventsFontFamily ?? "").trim();
  return resolveFontFamilyStack(stored || undefined, fallback);
};

/** Popup / event detail modal font (Appearance → Popup setting). */
export const resolvePopupFontFamily = (appearance, fallback = DEFAULT_FONT_STACK) => {
  return resolveFontFamilyStack(appearance?.popupFontFamily, fallback);
};

export function primaryFontNameFromStack(fontStack) {
  const stack = resolveFontFamilyStack(fontStack, "");
  if (!stack) return "";
  return stack.split(",")[0].trim().replace(/^['"]|['"]$/g, "");
}

export function collectGoogleFontFamiliesFromAppearance(appearance) {
  const set = new Set();
  if (!appearance || typeof appearance !== "object") return [];
  [resolveCalendarFontFamily(appearance, ""), resolveFontFamilyStack(appearance?.popupFontFamily, "")].forEach(
    (stack) => {
      const name = primaryFontNameFromStack(stack);
      if (name && GOOGLE_FONT_NAMES.has(name)) set.add(name);
    },
  );
  return [...set].sort();
}

const injectedFontKeys = new Set();

export function injectWidgetFonts(widgetKey, appearance) {
  if (typeof document === "undefined") return;
  const families = collectGoogleFontFamiliesFromAppearance(appearance);
  const key = String(widgetKey || "default");
  const id = `cw-fonts-${key.replace(/[^a-z0-9_-]/gi, "_")}`;
  const prev = document.getElementById(id);
  if (!families.length) {
    if (prev) prev.remove();
    injectedFontKeys.delete(id);
    return;
  }
  const familiesParam = families
    .map((f) => `family=${encodeURIComponent(f.replace(/ /g, "+"))}:wght@400;500;600;700`)
    .join("&");
  const href = `https://fonts.googleapis.com/css2?${familiesParam}&display=swap`;
  if (prev) {
    if (prev.getAttribute("href") !== href) prev.setAttribute("href", href);
    return;
  }
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = href;
  document.head.appendChild(link);
  injectedFontKeys.add(id);
}

export function normalizeAppearanceFontFamilies(appearance) {
  const out = { ...(appearance || {}) };
  Object.keys(out).forEach((k) => {
    if (k.endsWith("FontFamily")) out[k] = normalizeFontSelectValue(out[k]);
  });
  return out;
}
